import React, { useMemo } from 'react';
import styled from 'styled-components';
import BaseModalComponent from '../BaseModal.component';
import IconButtonComponent from '../../IconButton.component';
import TrashIcon from '../../Icons/Trash.icon';
import ButtonComponent from '../../Button.component';
import { useTranslation } from 'react-i18next';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import NotificationRemoveAlertComponent from '../../Alert/Notification/NotificationRemoveAlert.component';
import { ALERT_ACTION_ENUM } from '../../../types/action.types';
import ApiWrapper from '../../../hooks/useApi.hook';
import { Notification } from '../../../redux/Notification/NotificationType';
import { removeEventThunk } from '../../../redux/Event/Event.thunk';
import { getDateTime } from '../../../services/date.service';
import { DATE_FORMAT, TIME_FORMAT } from '../../../constants/date.contant';
import { RoutesConstant } from '../../../constants/routes.constant';

interface EventDetailsModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  event: Notification;
  callbackFn: Function;
}

const StyledEventDetailsModal = styled.div``;
const StyledEventDetailsBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 500px;
  margin-bottom: 24px;
`;
const StyledEventDetailsContent = styled.div`
  display: block;
  width: 80%;
`;
const StyledEventDetailsTitle = styled.h2`
  white-space: break-spaces;
  word-break: break-word;
`;
const StyledEventDetailsTime = styled.div`
  display: flex;
  padding-bottom: 10px;
`;
const StyledEventDetailsTitles = styled.div`
  margin-right: 20px;
`;
const StyledEventDetailsFooter = styled.div`
  display: flex;
  justify-content: end;
  padding: 8px 0 0;
`;
const StyledEventDetailsFooterButton = styled.div`
  margin-left: 10px;
`;

const ICON_BUTTON_SIZE = '50px';

const EventDetailsModalComponent = ({
  event,
  callbackFn,
}: EventDetailsModalProps) => {
  const { t } = useTranslation();
  const { closeModal, openModal } = useModalManager();
  const eventType = t(`notifications.types.${event.type}`);

  const handleConfirmDelete = (action?: ALERT_ACTION_ENUM) => {
    if (!action || action !== ALERT_ACTION_ENUM.YES) return;
    ApiWrapper(removeEventThunk(event.uuid)).then((res: boolean) => {
      if (res) {
        callbackFn();
        closeModal();
      }
    });
  };

  const handleOpenCandidate = (): void => {
    window.open(
      `${RoutesConstant.CANDIDATES}/all${event.notification?.path}`,
      '_blank',
    );
  };

  const renderFooter = useMemo(() => {
    return (
      <StyledEventDetailsFooter>
        <StyledEventDetailsFooterButton>
          <ButtonComponent
            appearance="default"
            handleClick={handleOpenCandidate}
            disabled={!event.notification}
          >
            {t('Open in new tab')}
          </ButtonComponent>
        </StyledEventDetailsFooterButton>
        <StyledEventDetailsFooterButton>
          <ButtonComponent
            appearance="primary"
            handleClick={() => closeModal()}
          >
            {t('Close')}
          </ButtonComponent>
        </StyledEventDetailsFooterButton>
      </StyledEventDetailsFooter>
    );
  }, [event]);

  return (
    <StyledEventDetailsModal>
      <BaseModalComponent footer={renderFooter} hasHeader={false}>
        <StyledEventDetailsBody>
          <StyledEventDetailsContent>
            <StyledEventDetailsTitle>
              {event.notification?.title
                ? `${event.notification?.title}: ${eventType}`
                : eventType}
            </StyledEventDetailsTitle>
            <StyledEventDetailsTime>
              <StyledEventDetailsTitles>
                <div>{`${t('Type')}: `}</div>
                <div>{`${t('Date')}: `}</div>
                <div>{`${t('Time')}: `}</div>
              </StyledEventDetailsTitles>
              <div>
                <div>{eventType}</div>
                <div>{getDateTime(event.sendAt, DATE_FORMAT)}</div>
                <div>{getDateTime(event.sendAt, TIME_FORMAT)}</div>
              </div>
            </StyledEventDetailsTime>
          </StyledEventDetailsContent>
          <IconButtonComponent
            width={ICON_BUTTON_SIZE}
            height={ICON_BUTTON_SIZE}
            onClick={() =>
              openModal(NotificationRemoveAlertComponent, {
                handleConfirm: handleConfirmDelete,
              })
            }
            disabled={!event.notification}
          >
            <TrashIcon />
          </IconButtonComponent>
        </StyledEventDetailsBody>
      </BaseModalComponent>
    </StyledEventDetailsModal>
  );
};

export default EventDetailsModalComponent;
